import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";

/** Departments (2026-10-22): a real list, so a department can exist before
 *  its first project and Admin can add / rename / retire. Projects,
 *  initiatives and enhancements still carry the name as text; a rename
 *  cascades server-side. */
export interface Department {
  name: string;
  /** Projects here don't need PMO approval (saved Approved, off the board). */
  self_managed: boolean;
  projects: number;
  initiatives: number;
  enhancements: number;
  created_at: string | null;
}

export const DEPARTMENTS_KEY = ["departments"] as const;

export function useDepartments() {
  return useQuery<Department[]>({ queryKey: DEPARTMENTS_KEY, queryFn: () => api.get("/departments").then((r) => r.data), staleTime: 30_000 });
}

function useAfterChange() {
  const qc = useQueryClient();
  return (rows: Department[]) => {
    qc.setQueryData(DEPARTMENTS_KEY, rows);
    qc.invalidateQueries({ queryKey: ["portfolio"] });
    qc.invalidateQueries({ queryKey: ["support-queue"] });
  };
}

export function useAddDepartment() {
  const after = useAfterChange();
  return useMutation<Department[], Error, string>({ mutationFn: (name) => api.post("/departments", { name }).then((r) => r.data), onSuccess: after });
}

export function useRenameDepartment() {
  const after = useAfterChange();
  return useMutation<Department[], Error, { from: string; to: string }>({
    mutationFn: ({ from, to }) => api.patch(`/departments/${encodeURIComponent(from)}`, { name: to }).then((r) => r.data),
    onSuccess: after,
  });
}

export function useDeleteDepartment() {
  const after = useAfterChange();
  return useMutation<Department[], Error, string>({ mutationFn: (name) => api.delete(`/departments/${encodeURIComponent(name)}`).then((r) => r.data), onSuccess: after });
}

export function useSetDepartmentSelfManaged() {
  const after = useAfterChange();
  return useMutation<Department[], Error, { name: string; self_managed: boolean }>({
    mutationFn: ({ name, self_managed }) => api.patch(`/departments/${encodeURIComponent(name)}`, { self_managed }).then((r) => r.data),
    onSuccess: after,
  });
}
